<template>
  <div class="report-display bg-white dark:bg-gray-800 rounded-xl border border-gray-200 dark:border-gray-700 overflow-hidden">
    <!-- Report Header -->
    <div class="report-header bg-gray-50 dark:bg-gray-900 p-6 border-b border-gray-200 dark:border-gray-700">
      <div class="flex items-start justify-between">
        <div class="flex-1">
          <h1 class="text-2xl font-bold text-gray-900 dark:text-white mb-2">
            {{ report.entityName }} - Risk Analysis Report
          </h1>
          
          <div class="flex flex-wrap items-center gap-4 text-sm text-gray-600 dark:text-gray-400">
            <div class="flex items-center gap-2">
              <span class="font-medium">Report Type:</span>
              <span class="px-2 py-1 bg-blue-100 text-blue-800 dark:bg-blue-900/20 dark:text-blue-400 rounded text-xs font-medium capitalize">
                {{ getReportTypeLabel(report.reportType) }}
              </span>
            </div>
            
            <div class="flex items-center gap-2">
              <span class="font-medium">Status:</span>
              <span :class="{
                'bg-green-100 text-green-800 dark:bg-green-900/20 dark:text-green-400': report.status === 'completed',
                'bg-yellow-100 text-yellow-800 dark:bg-yellow-900/20 dark:text-yellow-400': report.status === 'generating',
                'bg-blue-100 text-blue-800 dark:bg-blue-900/20 dark:text-blue-400': report.status === 'under_review',
                'bg-purple-100 text-purple-800 dark:bg-purple-900/20 dark:text-purple-400': report.status === 'approved'
              }" class="px-2 py-1 rounded text-xs font-medium capitalize">
                {{ report.status }}
              </span>
            </div>
            
            <div v-if="report.metadata?.generatedAt" class="flex items-center gap-2">
              <span class="font-medium">Generated:</span>
              <span>{{ formatDate(report.metadata.generatedAt) }}</span>
            </div>
          </div>
        </div>
        
        <!-- Quality Badge -->
        <div v-if="report.quality" class="ml-6">
          <div :class="getQualityColor(report.quality.rating)" class="px-4 py-2 border rounded-lg">
            <div class="text-center">
              <div class="text-lg font-bold">{{ report.quality.overallScore }}/100</div>
              <div class="text-xs font-medium uppercase">{{ report.quality.rating }}</div>
            </div>
          </div>
        </div>
      </div>
      
      <!-- Report Metrics -->
      <div v-if="report.quality || report.metadata" class="grid grid-cols-2 md:grid-cols-4 gap-4 mt-4 pt-4 border-t border-gray-200 dark:border-gray-700">
        <div v-if="report.quality?.footnoteCoverage" class="text-center">
          <div class="text-lg font-semibold text-gray-900 dark:text-white">{{ report.quality.footnoteCoverage }}</div>
          <div class="text-xs text-gray-600 dark:text-gray-400">Citation Coverage</div>
        </div>
        
        <div v-if="report.quality?.verifiedSources !== undefined" class="text-center">
          <div class="text-lg font-semibold text-gray-900 dark:text-white">
            {{ report.quality.verifiedSources }}/{{ report.quality.totalSources }}
          </div>
          <div class="text-xs text-gray-600 dark:text-gray-400">Verified Sources</div>
        </div>
        
        <div v-if="report.metadata?.wordCount" class="text-center">
          <div class="text-lg font-semibold text-gray-900 dark:text-white">{{ report.metadata.wordCount.toLocaleString() }}</div>
          <div class="text-xs text-gray-600 dark:text-gray-400">Words</div>
        </div>
        
        <div v-if="report.metadata?.sectionCount" class="text-center">
          <div class="text-lg font-semibold text-gray-900 dark:text-white">{{ report.metadata.sectionCount }}</div>
          <div class="text-xs text-gray-600 dark:text-gray-400">Sections</div>
        </div>
      </div>
    </div>
    
    <!-- Report Content -->
    <div class="report-content p-6">
      <!-- Executive Summary -->
      <div v-if="report.content?.executiveSummary" class="mb-8">
        <h2 class="text-xl font-bold text-gray-900 dark:text-white mb-4 flex items-center gap-2">
          <span class="w-1 h-6 bg-blue-500 rounded"></span>
          Executive Summary
        </h2>
        <div 
          class="text-gray-700 dark:text-gray-300 leading-relaxed whitespace-pre-line"
          v-html="formatExecutiveSummary(report.content.executiveSummary)"
        ></div>
      </div>
      
      <!-- Report Sections -->
      <div v-if="report.content?.sections && report.content.sections.length > 0" class="space-y-8">
        <div 
          v-for="(section, index) in report.content.sections" 
          :key="index"
          class="report-section"
        >
          <h2 class="text-xl font-bold text-gray-900 dark:text-white mb-4 flex items-center gap-2">
            <span class="w-1 h-6 bg-green-500 rounded"></span>
            {{ section.title }}
          </h2>
          <div 
            class="prose prose-gray dark:prose-invert max-w-none text-gray-700 dark:text-gray-300 leading-relaxed"
            v-html="formatReportText(section.content)"
          ></div>
        </div>
      </div>
      
      <!-- Full Report (fallback if sections not available) -->
      <div v-else-if="report.content?.fullReport" class="report-full">
        <h2 class="text-xl font-bold text-gray-900 dark:text-white mb-4 flex items-center gap-2">
          <span class="w-1 h-6 bg-purple-500 rounded"></span>
          Full Report
        </h2>
        <div 
          class="prose prose-gray dark:prose-invert max-w-none text-gray-700 dark:text-gray-300 leading-relaxed"
          v-html="formatReportText(report.content.fullReport)"
        ></div>
      </div>
      
      <!-- No Content State -->
      <div v-else class="text-center py-12 text-gray-500 dark:text-gray-400">
        <div class="text-4xl mb-4">📄</div>
        <p>No report content available</p>
      </div>
    </div>
    
    <!-- Footnotes Section -->
    <div v-if="report.content?.footnotes && report.content.footnotes.length > 0" class="footnotes-section bg-gray-50 dark:bg-gray-900 p-6 border-t border-gray-200 dark:border-gray-700">
      <h2 class="text-lg font-bold text-gray-900 dark:text-white mb-4 flex items-center gap-2">
        <span class="w-1 h-5 bg-orange-500 rounded"></span>
        References ({{ report.content.footnotes.length }})
      </h2>
      
      <div class="space-y-3">
        <div 
          v-for="(footnote, index) in report.content.footnotes" 
          :key="index"
          class="footnote-item p-3 bg-white dark:bg-gray-800 rounded-lg border border-gray-200 dark:border-gray-700"
        >
          <div class="flex items-start gap-3">
            <div class="footnote-number shrink-0 w-6 h-6 bg-blue-100 dark:bg-blue-900/20 text-blue-800 dark:text-blue-400 rounded-full text-xs font-bold flex items-center justify-center">
              {{ footnote.number || (index + 1) }}
            </div>
            
            <div class="footnote-content flex-1 min-w-0">
              <span
                v-if="footnote.sourceTierLabel"
                :class="getTierClass(footnote.sourceTierLabel)"
                class="inline-block mb-1 px-2 py-0.5 rounded text-xs font-medium"
                :title="getTierHint(footnote.sourceTierLabel)"
              >{{ getTierText(footnote.sourceTierLabel) }}</span>
              <p class="text-sm text-gray-700 dark:text-gray-300 mb-1">
                {{ footnote.citation }}
              </p>
              
              <div v-if="footnote.url" class="footnote-link">
                <a 
                  :href="footnote.url" 
                  target="_blank" 
                  rel="noopener noreferrer"
                  class="inline-flex items-center gap-1 text-xs text-blue-600 dark:text-blue-400 hover:text-blue-800 dark:hover:text-blue-300 transition-colors"
                >
                  <svg class="w-3 h-3" fill="currentColor" viewBox="0 0 20 20">
                    <path fill-rule="evenodd" d="M12.586 4.586a2 2 0 112.828 2.828l-3 3a2 2 0 01-2.828 0 1 1 0 00-1.414 1.414 4 4 0 005.656 0l3-3a4 4 0 00-5.656-5.656l-1.5 1.5a1 1 0 101.414 1.414l1.5-1.5zm-5 5a2 2 0 012.828 0 1 1 0 101.414-1.414 4 4 0 00-5.656 0l-3 3a4 4 0 105.656 5.656l1.5-1.5a1 1 0 10-1.414-1.414l-1.5 1.5a2 2 0 11-2.828-2.828l3-3z" clip-rule="evenodd" />
                  </svg>
                  View Source
                </a>
              </div>
              
              <div v-if="footnote.sourceId" class="footnote-source-id text-xs text-gray-500 dark:text-gray-400 mt-1">
                Source ID: {{ footnote.sourceId }}
              </div>
            </div>
          </div>
        </div>
      </div>
    </div>
  </div>
</template>

<script setup>
import { computed } from 'vue'

// Props
const props = defineProps({
  report: {
    type: Object,
    required: true
  }
})

// Report type mapping
const reportTypeOptions = [
  { value: 'comprehensive', label: 'Comprehensive Report' },
  { value: 'compliance_summary', label: 'Compliance Summary' },
  { value: 'executive_brief', label: 'Executive Brief' },
  { value: 'regulatory_focus', label: 'Regulatory Focus' }
]

// Utility functions
const getReportTypeLabel = (reportType) => {
  const option = reportTypeOptions.find(opt => opt.value === reportType)
  return option?.label || reportType
}

const getQualityColor = (rating) => {
  const colors = {
    excellent: 'text-green-700 bg-green-50 border-green-200 dark:text-green-400 dark:bg-green-900/20 dark:border-green-800',
    good: 'text-blue-700 bg-blue-50 border-blue-200 dark:text-blue-400 dark:bg-blue-900/20 dark:border-blue-800',
    fair: 'text-yellow-700 bg-yellow-50 border-yellow-200 dark:text-yellow-400 dark:bg-yellow-900/20 dark:border-yellow-800',
    poor: 'text-red-700 bg-red-50 border-red-200 dark:text-red-400 dark:bg-red-900/20 dark:border-red-800'
  }
  return colors[rating] || 'text-gray-700 bg-gray-50 border-gray-200 dark:text-gray-400 dark:bg-gray-900/20 dark:border-gray-800'
}

// How much weight a reference deserves: primary records and filings, established outlets, or everything else
const getTierText = (label) => ({ primary: 'Primary source', company: 'Company-published', reputable: 'Reputable outlet', database: 'Business database', other: 'Other source' }[label] || '')
const getTierHint = (label) => ({
  primary: 'Official record or filing from a regulator, court or registry',
  company: 'Published by the subject itself. Reliable for what the company reports about itself, not independent evidence of its risks or reputation.',
  reputable: 'Established news or trade outlet',
  database: 'Compiled commercial database record (no public page). Treat as an estimate.',
  other: 'Blog, aggregator, commentary page or unknown site. Check key facts against primary sources.'
}[label] || '')
const getTierClass = (label) => ({
  primary: 'bg-green-100 text-green-800 dark:bg-green-900/30 dark:text-green-300',
  company: 'bg-slate-100 text-slate-800 dark:bg-slate-700/40 dark:text-slate-200',
  reputable: 'bg-blue-100 text-blue-800 dark:bg-blue-900/30 dark:text-blue-300',
  database: 'bg-purple-100 text-purple-800 dark:bg-purple-900/30 dark:text-purple-300',
  other: 'bg-amber-100 text-amber-800 dark:bg-amber-900/30 dark:text-amber-300'
}[label] || '')

const formatDate = (dateString) => {
  if (!dateString) return 'Unknown'
  return new Date(dateString).toLocaleDateString('en-US', {
    year: 'numeric',
    month: 'long',
    day: 'numeric',
    hour: '2-digit',
    minute: '2-digit'
  })
}

const formatExecutiveSummary = (text) => {
  if (!text) return ''
  
  // Escape HTML to prevent XSS
  const escapeHtml = (str) => {
    const div = document.createElement('div')
    div.textContent = str
    return div.innerHTML
  }
  
  // Remove "**Executive Summary**" title if it appears at the start
  let cleaned = text.replace(/^\*\*Executive Summary\*\*\s*\n*/i, '').trim()
  cleaned = cleaned.replace(/^Executive Summary\s*\n*/i, '').trim()
  
  // Escape HTML
  cleaned = escapeHtml(cleaned)
  
  // Just add newlines where needed, don't process markdown
  // Fix patterns where section labels are followed immediately by markdown headers
  cleaned = cleaned.replace(/:\s*(###)/g, ':\n\n$1')
  cleaned = cleaned.replace(/\.\.\.\s*(###)/g, '...\n\n$1')
  cleaned = cleaned.replace(/(###\s+[^\n]+?)\s+(####)/g, '$1\n\n$2')
  
  // Convert double line breaks to <br><br> for display
  cleaned = cleaned.replace(/\n\n+/g, '<br><br>')
  // Convert single line breaks to <br>
  cleaned = cleaned.replace(/\n/g, '<br>')
  
  return cleaned
}

const formatReportText = (text) => {
  if (!text) return ''
  
  // Escape HTML to prevent XSS
  const escapeHtml = (str) => {
    const div = document.createElement('div')
    div.textContent = str
    return div.innerHTML
  }
  
  // Escape the text first
  let formatted = escapeHtml(text)
  
  // Convert markdown-style headers (must be done before line break processing)
  formatted = formatted.replace(/^####\s+(.+)$/gm, '<h4 class="text-lg font-semibold mt-4 mb-2 text-gray-900 dark:text-white">$1</h4>')
  formatted = formatted.replace(/^###\s+(.+)$/gm, '<h3 class="text-xl font-semibold mt-5 mb-2 text-gray-900 dark:text-white">$1</h3>')
  formatted = formatted.replace(/^##\s+(.+)$/gm, '<h2 class="text-2xl font-bold mt-6 mb-3 text-gray-900 dark:text-white">$1</h2>')
  formatted = formatted.replace(/^#\s+(.+)$/gm, '<h1 class="text-3xl font-bold mt-8 mb-4 text-gray-900 dark:text-white">$1</h1>')
  
  // Convert bold text **text** to <strong>
  formatted = formatted.replace(/\*\*(.+?)\*\*/g, '<strong class="font-semibold text-gray-900 dark:text-white">$1</strong>')
  
  // Convert italic text *text* to <em> (but not if it's already part of **)
  formatted = formatted.replace(/(?<!\*)\*([^\*]+?)\*(?!\*)/g, '<em class="italic">$1</em>')
  
  // Convert bullet points
  formatted = formatted.replace(/^[\-\*]\s+(.+)$/gm, '<li class="ml-4">$1</li>')
  
  // Convert numbered lists
  formatted = formatted.replace(/^\d+\.\s+(.+)$/gm, '<li class="ml-4">$1</li>')
  
  // Wrap consecutive list items in ul/ol tags
  formatted = formatted.replace(/(<li class="ml-4">.+?<\/li>\s*)+/gs, (match) => {
    return `<ul class="list-disc ml-6 mb-4 space-y-1">${match}</ul>`
  })
  
  // Split by double line breaks to create paragraphs
  const blocks = formatted.split(/\n\n+/)
  
  formatted = blocks.map(block => {
    block = block.trim()
    if (!block) return ''
    
    // Don't wrap headers or lists in p tags
    if (block.startsWith('<h') || block.startsWith('<ul') || block.startsWith('<ol')) {
      return block
    }
    
    // Convert single line breaks within paragraphs to <br>
    block = block.replace(/\n/g, '<br>')
    
    // Wrap in paragraph tags
    return `<p class="mb-4">${block}</p>`
  }).join('\n')
  
  // Clean up empty paragraphs
  formatted = formatted.replace(/<p class="mb-4">\s*<\/p>/g, '')
  formatted = formatted.replace(/<p class="mb-4"><br><\/p>/g, '')
  
  return formatted
}
</script>

<style scoped>
/* Custom styles for report display */
.report-display {
  max-width: 100%;
  font-family: 'Inter', -apple-system, BlinkMacSystemFont, sans-serif;
}

.report-content {
  line-height: 1.7;
}

.footnote-number {
  font-variant-numeric: tabular-nums;
}

/* Prose styling for report content */
.prose :deep(p) {
  margin-bottom: 0.75rem;
}

.prose :deep(h1),
.prose :deep(h2),
.prose :deep(h3),
.prose :deep(h4) {
  margin-top: 1.25rem;
  margin-bottom: 0.5rem;
  font-weight: 600;
  color: inherit;
}

.prose :deep(ul),
.prose :deep(ol) {
  margin: 0.5rem 0;
  padding-left: 1.5rem;
}

.prose :deep(li) {
  margin-bottom: 0.25rem;
}

.prose :deep(blockquote) {
  margin: 1.5rem 0;
  padding-left: 1rem;
  border-left: 3px solid #e5e7eb;
  font-style: italic;
}

.dark .prose :deep(blockquote) {
  border-left-color: #4b5563;
}

.prose :deep(table) {
  width: 100%;
  margin: 1.5rem 0;
  border-collapse: collapse;
}

.prose :deep(th),
.prose :deep(td) {
  padding: 0.75rem;
  text-align: left;
  border-bottom: 1px solid #e5e7eb;
}

.dark .prose :deep(th),
.dark .prose :deep(td) {
  border-bottom-color: #4b5563;
}

.prose :deep(th) {
  font-weight: 600;
  background-color: #f9fafb;
}

.dark .prose :deep(th) {
  background-color: #1f2937;
}

/* Footnote superscript styling */
.prose :deep(sup) {
  color: #3b82f6;
  font-weight: 600;
  text-decoration: none;
  cursor: pointer;
}

.prose :deep(sup):hover {
  color: #1d4ed8;
}

.dark .prose :deep(sup) {
  color: #60a5fa;
}

.dark .prose :deep(sup):hover {
  color: #93c5fd;
}

/* Print styles */
@media print {
  .report-display {
    box-shadow: none;
    border: none;
    border-radius: 0;
  }
  
  .report-header {
    background: white !important;
    color: black !important;
  }
  
  .footnotes-section {
    background: white !important;
  }
  
  .footnote-item {
    background: white !important;
    border: 1px solid #ccc !important;
  }
}
</style>